import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { useParams, useNavigate, Link } from 'react-router-dom';
// Payment terms come from ONE shared list — this screen used to carry its own shorter list
// and pre-fill '30 Days', which stamped 30 Days onto approved subbies (and their POs).
import { paymentTermsOptions, paymentTermsLabel } from '../utils/paymentTerms';
import { toast } from 'react-toastify';
import { 
  FaArrowLeft, FaCheckCircle, FaTimes, FaEye, FaDownload,
  FaBuilding, FaUserTie, FaEnvelope, FaPhone, FaIdCard,
  FaTruck, FaTrailer, FaFileAlt, FaShieldAlt, FaUniversity,
  FaUsers, FaClock, FaSpinner, FaInfoCircle, FaExclamationTriangle,
  FaRegFilePdf, FaImage, FaCheck, FaBan, FaUndo,
  FaFilePdf, FaFileImage, FaExternalLinkAlt, FaDollarSign,
  FaGlobe, FaCalendarAlt, FaWrench, FaRoad, FaBriefcase,
  FaQuoteRight, FaMapMarkerAlt, FaFax, FaMobile
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';

// Tier-aware verification checklist: invited subbies skip tax clearance / ID / liveness
const SUBBIE_TIER_VERIFY_FIELDS = {
  self: [
    'cipc_verified', 'git_insurance_verified', 'public_liability_verified',
    'tax_clearance_verified', 'bank_confirmation_verified',
    'id_document_verified', 'liveness_verified',
    'trucks_verified', 'trailers_verified', 'drivers_verified'
  ],
  invited: [
    'cipc_verified', 'git_insurance_verified', 'public_liability_verified',
    'bank_confirmation_verified',
    'trucks_verified', 'trailers_verified', 'drivers_verified'
  ]
};

const getSubbieTier = (s) =>
  (s?.onboarding_source === 'invited' || (!s?.application_submitted_at && s?.onboarding_source !== 'self'))
    ? 'invited'
    : 'self';

function AdminSubbieVerificationDetail({ onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subbie, setSubbie] = useState(null);
  const [user, setUser] = useState({});
  const [processing, setProcessing] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  
  // ✅ Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  // ✅ Payment Terms state — starts EMPTY, then loads the subbie's actual terms.
  // It must never default to '30 Days': approving a subbie would then silently commit us
  // to terms nobody agreed. If nothing is recorded it stays blank ("Not set").
  const [paymentTerms, setPaymentTerms] = useState({
    payment_terms: ''
  });
  
  // ✅ VERIFICATION STATE
  const [verification, setVerification] = useState({
    cipc_verified: false,
    cipc_annual_verified: false,
    shareholder_verified: false,
    tax_clearance_verified: false,
    git_insurance_verified: false,
    public_liability_verified: false,
    operator_card_verified: false,
    trucks_verified: false,
    trailers_verified: false,
    drivers_verified: false,
    id_document_verified: false,
    liveness_verified: false,
    bank_confirmation_verified: false,
    bank_statement_verified: false,
  });

  const [verificationNotes, setVerificationNotes] = useState({
    cipc_verified: '',
    cipc_annual_verified: '',
    shareholder_verified: '',
    tax_clearance_verified: '',
    git_insurance_verified: '',
    public_liability_verified: '',
    operator_card_verified: '',
    trucks_verified: '',
    trailers_verified: '',
    drivers_verified: '',
    id_document_verified: '',
    liveness_verified: '',
    bank_confirmation_verified: '',
    bank_statement_verified: '',
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchSubbieDetail();
  }, [id]);

  const fetchSubbieDetail = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/subcontractors/${id}`);
      const data = await response.json();
      if (data.success) {
        setSubbie(data.subcontractor);
        // Always mirror the stored value (blank when unset) so the select shows the truth.
        setPaymentTerms({ payment_terms: data.subcontractor.payment_terms || '' });
        if (data.subcontractor.verification_status === 'approved' || data.subcontractor.verification_status === 'rejected') {
          loadVerificationStates(data.subcontractor);
        }
      } else {
        toast.error(data.error || 'Failed to load application');
        navigate('/admin/subbie-verification');
      }
    } catch (err) {
      console.error('Error fetching subbie detail:', err);
      toast.error('Failed to load application');
      navigate('/admin/subbie-verification');
    } finally {
      setLoading(false);
    }
  };

  const loadVerificationStates = (data) => {
    if (data.verification_status === 'approved') {
      const allVerified = {};
      Object.keys(verification).forEach(key => {
        allVerified[key] = true;
      });
      setVerification(allVerified);
    }
  };

  const toggleVerify = (field) => {
    setVerification(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleVerifyNote = (field, value) => {
    setVerificationNotes(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getFullDocumentUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    const cleanUrl = url.startsWith('/') ? url : `/${url}`;
    return window.getServerUrl(cleanUrl);
  };

  const viewDocument = (docUrl, docName) => {
    if (!docUrl) {
      toast.warning('No document uploaded');
      return;
    }
    const fullUrl = getFullDocumentUrl(docUrl);
    if (fullUrl) {
      window.open(fullUrl, '_blank');
    } else {
      toast.error('Could not open document');
    }
  };

  const downloadDocument = (docUrl, docName) => {
    if (!docUrl) {
      toast.warning('No document to download');
      return;
    }
    const fullUrl = getFullDocumentUrl(docUrl);
    if (fullUrl) {
      window.open(fullUrl, '_blank');
    } else {
      toast.error('Could not download document');
    }
  };

  const getDocumentIcon = (url) => {
    if (!url) return <FaFileAlt />;
    if (url.toLowerCase().includes('.pdf')) return <FaFilePdf className="text-red-500" />;
    if (url.toLowerCase().includes('.jpg') || url.toLowerCase().includes('.jpeg') || url.toLowerCase().includes('.png')) {
      return <FaFileImage className="text-blue-500" />;
    }
    return <FaFileAlt />;
  };

  const getDocumentLabel = (url) => {
    if (!url) return 'No document';
    if (url.toLowerCase().includes('.pdf')) return 'PDF';
    if (url.toLowerCase().includes('.jpg') || url.toLowerCase().includes('.jpeg')) return 'JPEG';
    if (url.toLowerCase().includes('.png')) return 'PNG';
    return 'Document';
  };

  // ✅ HANDLE APPROVE - Opens modal
  const handleApprove = () => {
    const allVerified = Object.values(verification).every(v => v === true);
    
    if (!allVerified) {
      toast.warning('Please verify all sections before approving');
      return;
    }

    setShowApproveModal(true);
  };

  // ✅ CONFIRM APPROVE - Actually approves
  const confirmApprove = async () => {
    setProcessing(true);
    setShowApproveModal(false);
    
    try {
      const response = await fetch(`${API_URL}/subcontractors/${id}/verify`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'approved',
          admin_notes: adminNotes || 'All documents verified and approved.',
          admin_id: user.id,
          verification_details: verification,
          verification_notes: verificationNotes,
          payment_terms: paymentTerms.payment_terms || null
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('✅ Application approved successfully!');
        navigate('/admin/subbie-verification');
      } else {
        toast.error(data.error || 'Failed to approve application');
      }
    } catch (err) {
      console.error('Error approving application:', err);
      toast.error('Failed to approve application');
    } finally {
      setProcessing(false);
    }
  };

  // ✅ HANDLE REJECT - Opens modal
  const handleRejectClick = () => {
    setShowRejectModal(true);
  };

  // ✅ CONFIRM REJECT - Actually rejects
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setProcessing(true);
    setShowRejectModal(false);
    
    try {
      const response = await fetch(`${API_URL}/subcontractors/${id}/verify`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'rejected',
          admin_notes: adminNotes || `Rejected. Reason: ${rejectReason}`,
          rejection_reason: rejectReason,
          admin_id: user.id
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('❌ Application rejected');
        setShowRejectModal(false);
        navigate('/admin/subbie-verification');
      } else {
        toast.error(data.error || 'Failed to reject application');
      }
    } catch (err) {
      console.error('Error rejecting application:', err);
      toast.error('Failed to reject application');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-yellow-100 text-yellow-600',
      'under_review': 'bg-blue-100 text-blue-600',
      'pending_docs': 'bg-orange-100 text-orange-600',
      'approved': 'bg-green-100 text-green-600',
      'rejected': 'bg-red-100 text-red-600',
      'suspended': 'bg-gray-100 text-gray-600'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || 'bg-gray-100 text-gray-600'}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'R 0.00';
    return `R ${parseFloat(amount).toFixed(2)}`;
  };

  // ✅ VERIFICATION ITEM COMPONENT
  const VerificationItem = ({ field, label, icon, docUrl, comparisonText }) => {
    const isVerified = verification[field];
    const note = verificationNotes[field];
    const hasDocument = !!docUrl;
    const docIcon = getDocumentIcon(docUrl);
    const docLabel = getDocumentLabel(docUrl);

    return (
      <div className="flex items-start gap-3 p-3 bg-white rounded-lg border" style={{ borderColor: isVerified ? '#00A86B' : '#E8ECEF' }}>
        <button
          onClick={() => toggleVerify(field)}
          className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
            isVerified ? 'bg-green-500 text-white' : 'border-2 border-gray-300 hover:border-green-500'
          }`}
        >
          {isVerified && <FaCheck size={12} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {icon}
            <span className="font-medium text-sm" style={{ color: '#0A1929' }}>{label}</span>
            {isVerified ? (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <FaCheckCircle size={12} /> Verified
              </span>
            ) : (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <FaClock size={12} /> Pending
              </span>
            )}
          </div>
          {comparisonText && (
            <p className="text-xs text-gray-500 mt-1">{comparisonText}</p>
          )}
          
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {hasDocument ? (
              <>
                <button
                  onClick={() => viewDocument(docUrl, label)}
                  className="text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1 rounded-lg flex items-center gap-1.5 transition-colors"
                >
                  {docIcon}
                  <span>View {docLabel}</span>
                  <FaExternalLinkAlt size={10} />
                </button>
                <button
                  onClick={() => downloadDocument(docUrl, label)}
                  className="text-xs bg-gray-50 text-gray-600 hover:bg-gray-100 px-3 py-1 rounded-lg flex items-center gap-1.5 transition-colors"
                >
                  <FaDownload size={10} />
                  <span>Download</span>
                </button>
                <span className="text-xs text-gray-400">✓ Uploaded</span>
              </>
            ) : (
              <span className="text-xs text-gray-400">⚠️ No document uploaded</span>
            )}
          </div>

          <input
            type="text"
            placeholder="Add verification note..."
            value={note}
            onChange={(e) => handleVerifyNote(field, e.target.value)}
            className="w-full mt-1.5 px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-green"
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout activeTab="subbie-verification" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading application details...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!subbie) {
    return (
      <AdminLayout activeTab="subbie-verification" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border" style={{ borderColor: '#E8ECEF' }}>
          <div className="text-4xl mb-4">📋</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Application Not Found</h2>
          <p className="text-gray-500">The application you're looking for doesn't exist.</p>
          <Link to="/admin/subbie-verification" className="mt-4 inline-block px-4 py-2 rounded-lg text-white" style={{ backgroundColor: '#00A86B' }}>
            Back to Applications
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const tier = getSubbieTier(subbie);
  const shownVerifyFields = SUBBIE_TIER_VERIFY_FIELDS[tier] || SUBBIE_TIER_VERIFY_FIELDS.self;
  const allVerified = shownVerifyFields.every(f => verification[f] === true);
  const verifiedCount = shownVerifyFields.filter(f => verification[f] === true).length;
  const totalItems = shownVerifyFields.length;

  return (
    <AdminLayout activeTab="subbie-verification" onLogout={onLogout} user={user}>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin/subbie-verification" className="p-2 rounded-lg hover:bg-gray-100 transition-colors" style={{ color: '#0A1929' }}>
            <FaArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#0A1929' }}>
              <FaBuilding className="inline mr-2" style={{ color: '#00A86B' }} />
              Subcontractor Verification
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              <span className="font-semibold" style={{ color: '#0A1929' }}>{subbie.company_name}</span>
              <span className={getStatusBadge(subbie.verification_status)}>
                {subbie.verification_status || 'pending'}
              </span>
              <span className="text-sm text-gray-500">
                Submitted: {formatDate(subbie.application_submitted_at)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">
            {verifiedCount}/{totalItems} Verified
          </span>
          <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${(verifiedCount / totalItems) * 100}%`,
                backgroundColor: allVerified ? '#00A86B' : '#F59E0B'
              }}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Profile + Verification */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* COMPANY INFORMATION */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
              <FaBuilding className="inline mr-2" style={{ color: '#00A86B' }} />
              Company Information
            </h2>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Company Name</span>
                <span className="font-medium text-navy">{subbie.company_name}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Trading Name</span>
                <span className="font-medium text-navy">{subbie.trading_name || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Registration Number</span>
                <span className="font-medium text-navy">{subbie.registration_number}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">VAT Number</span>
                <span className="font-medium text-navy">{subbie.vat_number || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Company Phone</span>
                <span className="font-medium text-navy">{subbie.company_phone || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Company Fax</span>
                <span className="font-medium text-navy">{subbie.company_fax || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Company Website</span>
                <span className="font-medium text-navy">{subbie.company_website || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Years in Business</span>
                <span className="font-medium text-navy">{subbie.years_in_business || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Operating Areas</span>
                <span className="font-medium text-navy">{subbie.operating_areas || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-500">Status</span>
                <span className="font-medium text-navy">{subbie.verification_status || 'pending'}</span>
              </div>
            </div>
          </div>

          {/* CONTACT PERSON */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
              <FaUserTie className="inline mr-2" style={{ color: '#00A86B' }} />
              Contact Person
            </h2>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Full Name</span>
                <span className="font-medium text-navy">{subbie.contact_person}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Position</span>
                <span className="font-medium text-navy">{subbie.position || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Email</span>
                <span className="font-medium text-navy">{subbie.contact_email}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-500">Phone</span>
                <span className="font-medium text-navy">{subbie.contact_phone}</span>
              </div>
            </div>
          </div>

          {/* ADDRESS */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
              <FaMapMarkerAlt className="inline mr-2" style={{ color: '#00A86B' }} />
              Address
            </h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Physical Address</span>
                <span className="font-medium text-navy text-right">{subbie.company_address || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-500">Postal Address</span>
                <span className="font-medium text-navy text-right">{subbie.postal_address || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* ✅ VERIFICATION CHECKLIST */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
              <FaShieldAlt className="inline mr-2" style={{ color: '#00A86B' }} />
              Verification Checklist
            </h2>
            {tier === 'invited' && (
              <p className="text-xs text-blue-700 mb-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                ℹ️ Admin-invited subcontractor — light verification (tax clearance / ID document / liveness are not required).
              </p>
            )}
            <div className="space-y-3">
              {/* Company */}
              <VerificationItem
                field="cipc_verified"
                label="CIPC Registration"
                icon={<FaFileAlt className="text-gray-400" />}
                docUrl={subbie.cipc_document_url}
                comparisonText={`Reg: ${subbie.registration_number}`}
              />
              {shownVerifyFields.includes('tax_clearance_verified') && (
                <VerificationItem
                  field="tax_clearance_verified"
                  label="Tax Clearance Certificate"
                  icon={<FaFileAlt className="text-gray-400" />}
                  docUrl={subbie.tax_clearance_certificate_url}
                />
              )}
              {/* Insurance */}
              <VerificationItem
                field="git_insurance_verified"
                label="GIT Insurance Certificate"
                icon={<FaShieldAlt className="text-gray-400" />}
                docUrl={subbie.git_insurance_url}
              />
              <VerificationItem
                field="public_liability_verified"
                label="Public Liability Insurance"
                icon={<FaShieldAlt className="text-gray-400" />}
                docUrl={subbie.public_liability_url}
              />
              {/* Fleet */}
              <VerificationItem
                field="trucks_verified"
                label={`Tractors (${subbie.total_tractors || 0})`}
                icon={<FaTruck className="text-gray-400" />}
                comparisonText={`${subbie.trucks?.length || 0} vehicles listed`}
              />
              <VerificationItem
                field="trailers_verified"
                label={`Trailers (${subbie.total_trailers || 0})`}
                icon={<FaTrailer className="text-gray-400" />}
                comparisonText={`${subbie.trailers?.length || 0} vehicles listed`}
              />
              <VerificationItem
                field="drivers_verified"
                label={`Drivers (${subbie.total_drivers || 0})`}
                icon={<FaUserTie className="text-gray-400" />}
                comparisonText={`${subbie.drivers?.length || 0} drivers listed`}
              />
              {/* Identity */}
              {shownVerifyFields.includes('id_document_verified') && (
                <VerificationItem
                  field="id_document_verified"
                  label="ID Document"
                  icon={<FaIdCard className="text-gray-400" />}
                  docUrl={subbie.id_document_url}
                  comparisonText={`ID: ${subbie.id_number}`}
                />
              )}
              {shownVerifyFields.includes('liveness_verified') && (
                <VerificationItem
                  field="liveness_verified"
                  label="Liveness Photo (Selfie)"
                  icon={<FaImage className="text-gray-400" />}
                  docUrl={subbie.liveness_photo_url}
                  comparisonText="Selfie matches ID document"
                />
              )}
              {/* Banking */}
              <VerificationItem
                field="bank_confirmation_verified"
                label="Bank Confirmation Letter"
                icon={<FaUniversity className="text-gray-400" />}
                docUrl={subbie.bank_confirmation_url}
                comparisonText={`Bank: ${subbie.bank_name || 'N/A'}`}
              />
            </div>
          </div>

          {/* INSURANCE DETAILS (Profile View) */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
              <FaShieldAlt className="inline mr-2" style={{ color: '#00A86B' }} />
              Insurance Details
            </h2>
            
            {/* GIT Insurance */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-medium text-navy mb-2">GIT Insurance (Goods in Transit)</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-500">Provider</span>
                  <span className="font-medium text-navy">{subbie.git_insurance_provider || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-500">Policy Number</span>
                  <span className="font-medium text-navy">{subbie.git_policy_number || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-500">Expiry Date</span>
                  <span className="font-medium text-navy">{formatDate(subbie.git_insurance_expiry)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-500">Cover Amount</span>
                  <span className="font-medium text-navy">{formatCurrency(subbie.git_cover_amount)}</span>
                </div>
              </div>
              {subbie.git_insurance_url && (
                <div className="mt-2 flex gap-2">
                  <button onClick={() => viewDocument(subbie.git_insurance_url, 'GIT Insurance')} className="text-xs text-blue-600 hover:underline flex items-center gap-1"><FaEye size={10} /> View Certificate</button>
                  <button onClick={() => downloadDocument(subbie.git_insurance_url, 'git_insurance.pdf')} className="text-xs text-green-600 hover:underline flex items-center gap-1"><FaDownload size={10} /> Download</button>
                </div>
              )}
            </div>

            {/* Public Liability */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-medium text-navy mb-2">Public Liability Insurance</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-500">Provider</span>
                  <span className="font-medium text-navy">{subbie.public_liability_provider || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-500">Policy Number</span>
                  <span className="font-medium text-navy">{subbie.public_liability_policy_number || 'N/A'}</span>
                </div>
                <div className="flex justify-between py-1 border-b border-gray-100">
                  <span className="text-gray-500">Expiry Date</span>
                  <span className="font-medium text-navy">{formatDate(subbie.public_liability_expiry)}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-500">Cover Amount</span>
                  <span className="font-medium text-navy">{formatCurrency(subbie.public_liability_cover_amount)}</span>
                </div>
              </div>
              {subbie.public_liability_url && (
                <div className="mt-2 flex gap-2">
                  <button onClick={() => viewDocument(subbie.public_liability_url, 'Public Liability')} className="text-xs text-blue-600 hover:underline flex items-center gap-1"><FaEye size={10} /> View Certificate</button>
                  <button onClick={() => downloadDocument(subbie.public_liability_url, 'public_liability.pdf')} className="text-xs text-green-600 hover:underline flex items-center gap-1"><FaDownload size={10} /> Download</button>
                </div>
              )}
            </div>

            {/* Tax Clearance */}
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-medium text-navy mb-2">Tax Clearance</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex justify-between py-1">
                  <span className="text-gray-500">Expiry Date</span>
                  <span className="font-medium text-navy">{formatDate(subbie.tax_clearance_expiry)}</span>
                </div>
              </div>
              {subbie.tax_clearance_certificate_url && (
                <div className="mt-2 flex gap-2">
                  <button onClick={() => viewDocument(subbie.tax_clearance_certificate_url, 'Tax Clearance')} className="text-xs text-blue-600 hover:underline flex items-center gap-1"><FaEye size={10} /> View Certificate</button>
                  <button onClick={() => downloadDocument(subbie.tax_clearance_certificate_url, 'tax_clearance.pdf')} className="text-xs text-green-600 hover:underline flex items-center gap-1"><FaDownload size={10} /> Download</button>
                </div>
              )}
            </div>
          </div>

          {/* BANKING DETAILS */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
              <FaUniversity className="inline mr-2" style={{ color: '#00A86B' }} />
              Banking Details
            </h2>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Bank Name</span>
                <span className="font-medium text-navy">{subbie.bank_name || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Branch Name</span>
                <span className="font-medium text-navy">{subbie.bank_branch || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Account Number</span>
                <span className="font-medium text-navy">{subbie.bank_account_number || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">Account Type</span>
                <span className="font-medium text-navy">{subbie.bank_account_type || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-500">Branch Code</span>
                <span className="font-medium text-navy">{subbie.bank_branch_code || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* REFERENCES */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
              <FaUsers className="inline mr-2" style={{ color: '#00A86B' }} />
              References
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-medium text-navy mb-2">Reference 1</h4>
                <div className="space-y-1 text-sm">
                  <p><span className="text-gray-500">Name:</span> {subbie.reference_1_name || 'N/A'}</p>
                  <p><span className="text-gray-500">Company:</span> {subbie.reference_1_company || 'N/A'}</p>
                  <p><span className="text-gray-500">Phone:</span> {subbie.reference_1_phone || 'N/A'}</p>
                  <p><span className="text-gray-500">Email:</span> {subbie.reference_1_email || 'N/A'}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-medium text-navy mb-2">Reference 2</h4>
                <div className="space-y-1 text-sm">
                  <p><span className="text-gray-500">Name:</span> {subbie.reference_2_name || 'N/A'}</p>
                  <p><span className="text-gray-500">Company:</span> {subbie.reference_2_company || 'N/A'}</p>
                  <p><span className="text-gray-500">Phone:</span> {subbie.reference_2_phone || 'N/A'}</p>
                  <p><span className="text-gray-500">Email:</span> {subbie.reference_2_email || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* IDENTITY */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
              <FaIdCard className="inline mr-2" style={{ color: '#00A86B' }} />
              Identity Verification
            </h2>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">ID Number</span>
                <span className="font-medium text-navy">{subbie.id_number || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">ID Document</span>
                {subbie.id_document_url ? (
                  <button onClick={() => viewDocument(subbie.id_document_url, 'ID Document')} className="text-xs text-blue-600 hover:underline flex items-center gap-1"><FaEye size={10} /> View</button>
                ) : <span className="text-gray-400">N/A</span>}
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-500">Liveness Photo</span>
                {subbie.liveness_photo_url ? (
                  <button onClick={() => viewDocument(subbie.liveness_photo_url, 'Liveness Photo')} className="text-xs text-blue-600 hover:underline flex items-center gap-1"><FaEye size={10} /> View</button>
                ) : <span className="text-gray-400">N/A</span>}
              </div>
            </div>
          </div>

          {/* FLEET SUMMARY */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: '#E8ECEF' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: '#0A1929' }}>
              <FaTruck className="inline mr-2" style={{ color: '#00A86B' }} />
              Fleet Summary
            </h2>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-gray-50 rounded-lg p-4"><p className="text-2xl font-bold text-navy">{subbie.total_tractors || 0}</p><p className="text-xs text-gray-500">Tractors</p></div>
              <div className="bg-gray-50 rounded-lg p-4"><p className="text-2xl font-bold text-navy">{subbie.total_trailers || 0}</p><p className="text-xs text-gray-500">Trailers</p></div>
              <div className="bg-gray-50 rounded-lg p-4"><p className="text-2xl font-bold text-navy">{subbie.total_drivers || 0}</p><p className="text-xs text-gray-500">Drivers</p></div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm mt-4">
              <div className="flex justify-between py-1 border-b border-gray-100"><span className="text-gray-500">Own Workshop</span><span className="font-medium text-navy">{subbie.has_own_workshop ? '✅ Yes' : '❌ No'}</span></div>
              <div className="flex justify-between py-1 border-b border-gray-100"><span className="text-gray-500">24hr Breakdown</span><span className="font-medium text-navy">{subbie.has_24hr_breakdown ? '✅ Yes' : '❌ No'}</span></div>
              <div className="flex justify-between py-1"><span className="text-gray-500">GPS Tracking</span><span className="font-medium text-navy">{subbie.has_tracking_system ? '✅ Yes' : '❌ No'}</span></div>
            </div>
            {subbie.preferred_route && <div className="mt-2 text-sm"><span className="text-gray-500">Preferred Route:</span><span className="font-medium text-navy ml-2">{subbie.preferred_route}</span></div>}
          </div>
        </div>

        {/* Right Column - Summary & Actions */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border p-6 sticky top-24" style={{ borderColor: '#E8ECEF' }}>
            <h3 className="font-semibold text-navy mb-4">Verification Summary</h3>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Verified Items</span><span className="font-medium" style={{ color: allVerified ? '#00A86B' : '#0A1929' }}>{verifiedCount}/{totalItems}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Status</span><span className={getStatusBadge(subbie.verification_status)}>{subbie.verification_status || 'pending'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Submitted</span><span className="text-xs">{formatDate(subbie.application_submitted_at)}</span></div>
              {subbie.verified_at && <div className="flex justify-between"><span className="text-gray-500">Verified</span><span className="text-xs">{formatDate(subbie.verified_at)}</span></div>}
            </div>

            <hr className="my-4" style={{ borderColor: '#E8ECEF' }} />

            {/* Payment Terms */}
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="text-sm font-medium text-blue-700 mb-2 flex items-center gap-2"><FaDollarSign className="text-blue-500" /> Payment Terms</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-gray-600">Current Terms</span><span className="font-medium text-blue-700">{paymentTermsLabel(subbie.payment_terms)}</span></div>
                {subbie.verification_status === 'pending' && (
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Set Payment Terms</label>
                    <select value={paymentTerms.payment_terms} onChange={(e) => setPaymentTerms({ ...paymentTerms, payment_terms: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-green bg-white" style={{ borderColor: '#E8ECEF' }}>
                      <option value="">Not set</option>
                      {paymentTermsOptions(subbie.payment_terms).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>

            <hr className="my-4" style={{ borderColor: '#E8ECEF' }} />

            {/* Admin Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
              <textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green text-sm" style={{ borderColor: '#E8ECEF' }} rows="3" placeholder="Add overall notes about this application..." />
            </div>

            <hr className="my-4" style={{ borderColor: '#E8ECEF' }} />

            {/* Action Buttons */}
            <div className="space-y-3">
              {subbie.verification_status === 'pending' && (
                <>
                  <button 
                    onClick={handleApprove} 
                    disabled={processing || !allVerified} 
                    className="w-full px-4 py-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2" 
                    style={{ backgroundColor: allVerified ? '#00A86B' : '#94A3B8' }} 
                    onMouseEnter={(e) => { if (allVerified) e.currentTarget.style.backgroundColor = '#00995E'; }} 
                    onMouseLeave={(e) => { if (allVerified) e.currentTarget.style.backgroundColor = '#00A86B'; }}
                  >
                    {processing ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
                    {processing ? 'Processing...' : '✅ Approve Application'}
                  </button>
                  {!allVerified && <p className="text-xs text-yellow-600 text-center"><FaExclamationTriangle className="inline mr-1" size={12} /> Verify all {totalItems - verifiedCount} remaining items before approving</p>}
                  <button 
                    onClick={handleRejectClick} 
                    disabled={processing} 
                    className="w-full px-4 py-3 rounded-lg text-white font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2" 
                    style={{ backgroundColor: '#EF4444' }} 
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#DC2626'} 
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#EF4444'}
                  >
                    <FaTimes /> Reject Application
                  </button>
                </>
              )}
              {subbie.verification_status === 'approved' && (
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <FaCheckCircle className="text-3xl text-green-500 mx-auto mb-2" />
                  <p className="text-green-700 font-medium">Application Approved</p>
                  <p className="text-xs text-green-600">Verified on {formatDate(subbie.verified_at)}</p>
                  {subbie.payment_terms && <p className="text-xs text-blue-600 mt-1">Payment Terms: <strong>{subbie.payment_terms}</strong></p>}
                </div>
              )}
              {subbie.verification_status === 'rejected' && (
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <FaTimes className="text-3xl text-red-500 mx-auto mb-2" />
                  <p className="text-red-700 font-medium">Application Rejected</p>
                  {subbie.rejection_reason && <p className="text-xs text-red-600 mt-1">Reason: {subbie.rejection_reason}</p>}
                </div>
              )}
            </div>

            <hr className="my-4" style={{ borderColor: '#E8ECEF' }} />
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-gray-50 rounded-lg p-2 text-center"><span className="text-gray-500">Tractors</span><p className="font-bold text-navy">{subbie.total_tractors || 0}</p></div>
              <div className="bg-gray-50 rounded-lg p-2 text-center"><span className="text-gray-500">Trailers</span><p className="font-bold text-navy">{subbie.total_trailers || 0}</p></div>
              <div className="bg-gray-50 rounded-lg p-2 text-center"><span className="text-gray-500">Drivers</span><p className="font-bold text-navy">{subbie.total_drivers || 0}</p></div>
              <div className="bg-gray-50 rounded-lg p-2 text-center"><span className="text-gray-500">Docs</span><p className="font-bold text-navy">{Object.keys(subbie).filter(k => k.includes('_url') && subbie[k]).length}</p></div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ STYLED APPROVE MODAL */}
      {showApproveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowApproveModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCheckCircle className="text-3xl text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-navy">Approve Application</h2>
              <p className="text-sm text-gray-500 mt-2">
                Are you sure you want to approve <strong className="text-navy">{subbie?.company_name}</strong>'s application?
              </p>
              {subbie?.payment_terms && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg text-left text-sm">
                  <p className="font-medium text-blue-700">Payment Terms:</p>
                  <p className="text-gray-600">Terms: <strong>{paymentTerms.payment_terms}</strong></p>
                </div>
              )}
              <p className="text-xs text-gray-400 mt-3">
                This action cannot be undone.
              </p>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowApproveModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                style={{ backgroundColor: 'white', color: '#4A5568' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmApprove}
                disabled={processing}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white hover:bg-green transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#00A86B' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00995E'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#00A86B'}
              >
                {processing ? (
                  <>
                    <span className="animate-spin">⏳</span> Processing...
                  </>
                ) : (
                  <>
                    <FaCheckCircle size={14} /> Approve
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ STYLED REJECT MODAL */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowRejectModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaTimes className="text-3xl text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-red-600">Reject Application</h2>
              <p className="text-sm text-gray-500 mt-2">
                Please provide a reason for rejecting <strong className="text-navy">{subbie?.company_name}</strong>'s application.
              </p>
            </div>
            
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-red-500 text-sm"
              rows="4"
              placeholder="Explain why this application is being rejected..."
            />
            
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                style={{ backgroundColor: 'white', color: '#4A5568' }}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processing}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#EF4444' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#DC2626'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#EF4444'}
              >
                {processing ? (
                  <>
                    <span className="animate-spin">⏳</span> Processing...
                  </>
                ) : (
                  <>
                    <FaTimes size={14} /> Reject
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminSubbieVerificationDetail;